import React, {useCallback, useContext, useEffect, useMemo, useReducer, useState} from 'react';
import {Button, Container, IconButton, InputAdornment, Menu, MenuItem, Snackbar, TextField, Tooltip, Typography} from "@mui/material";
import SearchTwoToneIcon from "@mui/icons-material/SearchTwoTone";
import ArrowForwardTwoToneIcon from "@mui/icons-material/ArrowForwardTwoTone";
import ArrowBackTwoToneIcon from "@mui/icons-material/ArrowBackTwoTone";
import CalendarMonthTwoToneIcon from "@mui/icons-material/CalendarMonthTwoTone";
import AdminPanelSettingsTwoToneIcon from "@mui/icons-material/AdminPanelSettingsTwoTone";
import DarkModeTwoToneIcon from "@mui/icons-material/DarkModeTwoTone";
import LightModeTwoToneIcon from "@mui/icons-material/LightModeTwoTone";
import GTranslateTwoToneIcon from '@mui/icons-material/GTranslateTwoTone';
import {useHistory} from "react-router-dom";
import MuiAlert from '@mui/material/Alert';
import useMediaQuery from "@mui/material/useMediaQuery";
import {ContattaciStyle} from "../../styles/pages/contact/ContattaciStyle";
import {DynamicIcon} from "../../utils/dictionaries/Categories";
import {searchFaq} from "../../services/FaqServices";
import {FAQ_INITIAL_STATE, FaqReducer, initFaqState} from "../../reducers/FaqReducer";
import AccordionFaq from "../gestione/faq/AccordionFaq";
import {setCloseSnackbar, setLang, setThemeType} from "../../services/AppServices";
import {AppContext, CategoriesContext} from "../../App";
import {langDict, languages} from "../../strings/LanguageDictionary";

const getCategorySlug = (category) => category
    .toLowerCase()
    .replaceAll(" ", "_")
    .replaceAll("-", "_")
    .replaceAll("/", "_")
    .replaceAll("à", "a")
    .replaceAll("(", "_")
    .replaceAll(")", "_");

const PUBLIC_CATEGORY_FALLBACKS = [
    {
        key: "public-info",
        label: "Informazioni generali",
        description: "Apri richiesta",
        icon: "InfoTwoTone",
        color: "#00a896",
        iconColor: "#ffffff",
    },
    {
        key: "public-faq",
        label: "Domande frequenti",
        description: "Consulta risposte",
        icon: "QuestionAnswerTwoTone",
        color: "#2f80ed",
        iconColor: "#ffffff",
    },
    {
        key: "public-documents",
        label: "Documenti e pratiche",
        description: "Invia dettagli",
        icon: "AssignmentTwoTone",
        color: "#f2a900",
        iconColor: "#1f2d3d",
    },
    {
        key: "public-support",
        label: "Assistenza pratica",
        description: "Chiedi supporto",
        icon: "SupportAgentTwoTone",
        color: "#7c4dff",
        iconColor: "#ffffff",
    },
];

const FloatingVisual = ({accent, className, children}) => (
    <span className={className} style={{"--visual-accent": accent}}>
        <svg viewBox="0 0 120 120" aria-hidden="true">
            {children}
        </svg>
    </span>
);

const LandingVisuals = ({classes, compact = false}) => (
    <div className={`${classes.visualStage} ${compact ? classes.mobileVisualStage : ""}`} aria-hidden="true">
        <FloatingVisual accent="#00b19f" className={`${classes.visualCard} ${classes.visualCardPerson}`}>
            <circle cx="60" cy="36" r="18" fill="var(--visual-accent)" opacity="0.92"/>
            <path d="M30 103c4-25 16-40 30-40s26 15 30 40" fill="var(--visual-accent)" opacity="0.58"/>
            <path d="M36 39c6-18 22-28 41-17" fill="none" stroke="var(--atl-text)" strokeWidth="6" strokeLinecap="round" opacity="0.72"/>
            <path d="M82 38c12 3 18 10 18 20s-6 17-18 20" fill="none" stroke="var(--atl-blue)" strokeWidth="6" strokeLinecap="round"/>
            <circle cx="82" cy="78" r="5" fill="var(--atl-blue)"/>
        </FloatingVisual>
        <FloatingVisual accent="#304ffe" className={`${classes.visualCard} ${classes.visualCardDocument}`}>
            <rect x="24" y="18" width="72" height="86" rx="16" fill="var(--visual-accent)" opacity="0.14"/>
            <path d="M40 42h42M40 61h31M40 80h23" stroke="var(--atl-text)" strokeWidth="7" strokeLinecap="round" opacity="0.5"/>
            <path d="m71 79 9 9 19-25" fill="none" stroke="var(--visual-accent)" strokeWidth="8" strokeLinecap="round" strokeLinejoin="round"/>
        </FloatingVisual>
        <FloatingVisual accent="#ffb84d" className={`${classes.visualCard} ${classes.visualCardCalendar}`}>
            <rect x="22" y="26" width="76" height="68" rx="14" fill="var(--visual-accent)" opacity="0.22"/>
            <path d="M22 48h76M43 18v20M77 18v20" stroke="var(--visual-accent)" strokeWidth="8" strokeLinecap="round"/>
            <path d="m45 72 9 9 22-27" fill="none" stroke="var(--atl-green)" strokeWidth="8" strokeLinecap="round" strokeLinejoin="round"/>
        </FloatingVisual>
        <FloatingVisual accent="#7c4dff" className={`${classes.visualCard} ${classes.visualCardMessage}`}>
            <path d="M22 31h76v48H55L35 97V79H22Z" fill="var(--visual-accent)" opacity="0.2"/>
            <path d="M42 52h37M42 68h25" stroke="var(--atl-text)" strokeWidth="7" strokeLinecap="round" opacity="0.55"/>
        </FloatingVisual>
        <FloatingVisual accent="#00a7d8" className={`${classes.visualCard} ${classes.visualCardChart}`}>
            <path d="M25 91h70" stroke="var(--atl-text)" strokeWidth="7" strokeLinecap="round" opacity="0.38"/>
            <rect x="31" y="56" width="16" height="35" rx="7" fill="var(--visual-accent)" opacity="0.72"/>
            <rect x="54" y="38" width="16" height="53" rx="7" fill="var(--visual-accent)" opacity="0.92"/>
            <rect x="77" y="24" width="16" height="67" rx="7" fill="var(--visual-accent)" opacity="0.58"/>
        </FloatingVisual>
        <FloatingVisual accent="#ff5a7a" className={`${classes.visualCard} ${classes.visualCardPlus}`}>
            <circle cx="60" cy="60" r="38" fill="var(--visual-accent)" opacity="0.16"/>
            <path d="M60 37v46M37 60h46" stroke="var(--visual-accent)" strokeWidth="12" strokeLinecap="round"/>
        </FloatingVisual>
    </div>
);

const CategoryButton = ({category, classes, compact = false, onClick}) => (
    <button
        className={`${classes.categoryCard} ${compact ? classes.mobileCategoryCard : ""}`}
        onClick={onClick}
        style={{
            "--landing-card-color": getCategoryAccentColor(category) || "var(--atl-blue)",
            "--landing-icon-color": category.iconColor || getCategoryAccentColor(category) || "var(--atl-blue)",
            "--landing-icon-accent": getCategoryIconAccentColor(category) || getCategoryAccentColor(category) || "var(--atl-blue)",
        }}
        type="button"
    >
        <span className={classes.categoryIcon}>
            {category.iconNode || <DynamicIcon iconName={category.icon} iconColor={category.iconColor} fontSize={compact ? 28 : 34}/>}
        </span>
        <span className={classes.categoryBody}>
            <strong>{category.label}</strong>
        </span>
        <ArrowForwardTwoToneIcon className={classes.categoryArrow}/>
    </button>
);

const getVisibleSubCategories = (category) => (
    category?.subCategories?.filter((subCategory) => (
        subCategory?.label && subCategory.enabled !== false && subCategory.visible !== false
    )) || []
);

const isWhiteLikeColor = (color) => (
    color && ["#fff", "#ffffff", "white", "rgb(255,255,255)", "rgb(255, 255, 255)"].includes(color.toLowerCase())
);

const getCategoryAccentColor = (category) => (
    isWhiteLikeColor(category.color) ? category.iconColor : category.color
);

const getCategoryIconAccentColor = (category) => (
    isWhiteLikeColor(category.iconColor) ? getCategoryAccentColor(category) : category.iconColor
);

const LandingPage = () => {
    const [faqState, faqDispatch] = useReducer(FaqReducer, FAQ_INITIAL_STATE, initFaqState);
    const [value, setValue] = useState("");
    const [anchorEl, setAnchorEl] = useState(null);
    const [selectedCategory, setSelectedCategory] = useState(null);
    const history = useHistory();
    const appContext = useContext(AppContext);
    const categoriesContext = useContext(CategoriesContext);
    const classes = ContattaciStyle();
    const isMobile = useMediaQuery("(max-width:720px)");

    const categories = useMemo(() => {
        const publicCategories = Object.entries(categoriesContext?.categoriesV3 || {})
            .map(([key, category]) => ({key, ...category}))
            .filter((category) => category.label)
            .sort((first, second) => first.label.localeCompare(second.label));

        return publicCategories.length ? publicCategories : PUBLIC_CATEGORY_FALLBACKS;
    }, [categoriesContext?.categoriesV3]);

    const selectCategory = useCallback((category) => {
        const subCategories = getVisibleSubCategories(category);

        if (subCategories.length > 0) {
            setValue("");
            setSelectedCategory(category);
            return;
        }

        history.push(`/contattaci/${getCategorySlug(category.label)}`);
    }, [history]);

    const selectSubCategory = useCallback((subCategory) => {
        if (!selectedCategory) return;

        history.push({
            pathname: `/contattaci/${getCategorySlug(selectedCategory.label)}/ticket`,
            state: {sottoCategoria: subCategory.label},
        });
    }, [history, selectedCategory]);

    const categoryItems = useMemo(() => ([
        ...categories.map((category) => ({
            ...category,
            description: category.description || "Apri richiesta",
            onClick: () => selectCategory(category),
        })),
        {
            key: "reservation",
            label: "Prenotazioni",
            description: "Gestisci appuntamento",
            color: "#3B82F6",
            iconColor: "#1D4ED8",
            iconNode: <CalendarMonthTwoToneIcon/>,
            onClick: () => history.push("/prenota"),
        },
    ]), [categories, history, selectCategory]);

    const subCategoryItems = useMemo(() => (
        getVisibleSubCategories(selectedCategory).map((subCategory) => ({
            ...subCategory,
            key: subCategory.id || subCategory.label,
            label: subCategory.label,
            description: "Apri richiesta",
            color: selectedCategory?.color || "var(--atl-blue)",
            icon: selectedCategory?.icon,
            iconColor: selectedCategory?.iconColor,
            onClick: () => selectSubCategory(subCategory),
        }))
    ), [selectedCategory, selectSubCategory]);

    const faqResults = useMemo(() => {
        const terms = value
            .split(" ")
            .map((term) => term.trim())
            .filter((term) => term.length >= 3);

        if (!terms.length) return [];

        return faqState.faqs?.filter((item) => {
            const keywords = item?.keywords?.split(",") || [];
            const questionWords = item?.domanda?.split(" ") || [];
            const searchable = [...keywords, ...questionWords]
                .map((term) => term.trim())
                .filter((term) => term.length >= 3);

            return terms.some((term) => searchable.some((candidate) => (
                term.toUpperCase().includes(candidate.toUpperCase()) ||
                candidate.toUpperCase().includes(term.toUpperCase())
            )));
        }) || [];
    }, [faqState.faqs, value]);

    const handleLanguageClose = (key) => {
        if (key) {
            setLang(key, appContext.dispatch);
        }
        setAnchorEl(null);
    };

    const handleCloseSnack = useCallback(() => {
        setCloseSnackbar(appContext.dispatch);
    }, [appContext.dispatch]);

    const toggleTheme = () => {
        setThemeType(
            appContext.state.theme.palette.mode === "light" ? "dark" : "light",
            appContext.state.theme,
            appContext.dispatch
        );
    };

    useEffect(() => {
        window.scrollTo(0, 0);
        searchFaq({}, faqDispatch);
    }, [faqDispatch]);

    const searchBox = (
        <div className={`${classes.heroSearch} ${classes.sectionSearch} ${isMobile ? classes.mobileSearchDock : ""}`}>
            <TextField
                autoFocus={false}
                fullWidth
                onChange={(event) => setValue(event.target.value)}
                placeholder={languages[appContext.state.lang].CONTATTACI_DESCR_PROBL_INPUT_}
                value={value}
                variant="outlined"
                slotProps={{
                    input: {
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchTwoToneIcon/>
                            </InputAdornment>
                        ),
                    },
                }}
            />
            {value && (
                <Button className={classes.clearButton} onClick={() => setValue("")}>
                    Cancella
                </Button>
            )}
        </div>
    );

    const faqSection = (
        <section className={`${classes.contentSection} ${classes.landingPathsSection} ${isMobile ? classes.mobileContentSection : ""}`} aria-label="Risultati FAQ">
            <div className={`${classes.sectionHeading} ${classes.categoryMainHeading} ${isMobile ? classes.mobileSectionHeading : ""}`}>
                <div>
                    <Typography component="h2" className={classes.sectionTitle}>
                        Risultati della ricerca
                    </Typography>
                </div>
                {searchBox}
            </div>
            <div className={`${classes.faqPanel} ${isMobile ? classes.mobileFaqPanel : ""}`}>
                {!faqState.loadingSearchFaq && !faqState.errorSearchFaq && faqResults.length > 0 && (
                    <Typography className={classes.faqHelperText}>
                        Risposte trovate in base alla tua ricerca.
                    </Typography>
                )}
                {faqState.loadingSearchFaq && (
                    <Typography className={classes.emptyState}>Sto cercando...</Typography>
                )}
                {!faqState.loadingSearchFaq && faqState.errorSearchFaq && (
                    <Typography className={classes.emptyState}>Ops, c'e stato un errore. Aggiorna la pagina e riprova.</Typography>
                )}
                {!faqState.loadingSearchFaq && !faqState.errorSearchFaq && faqResults.length === 0 && (
                    <Typography className={classes.emptyState}>Nessuna FAQ trovata per questa ricerca.</Typography>
                )}
                {!faqState.loadingSearchFaq && !faqState.errorSearchFaq && faqResults.map((faq, index) => (
                    <AccordionFaq key={`${faq.domanda}-${index}`} risposta={faq.risposta} domanda={faq.domanda} contattaci/>
                ))}
            </div>
        </section>
    );

    return (
        <React.Fragment>
            <main className={classes.landingPage}>
                <div className={classes.topFloatingActions}>
                    <Button
                        className={classes.loginButton}
                        onClick={() => history.push("/gestione/login")}
                        startIcon={<AdminPanelSettingsTwoToneIcon/>}
                    >
                        Area riservata
                    </Button>
                </div>

                <div className={classes.utilityDock} aria-label="Impostazioni rapide">
                    <Tooltip title={languages[appContext.state.lang].CONTATTACI_CHANGE_LANG_BUTTON} placement="left">
                        <IconButton
                            aria-label={languages[appContext.state.lang].CONTATTACI_CHANGE_LANG_BUTTON}
                            className={classes.utilityButton}
                            onClick={(event) => setAnchorEl(event.currentTarget)}
                            size="large"
                        >
                            <GTranslateTwoToneIcon/>
                        </IconButton>
                    </Tooltip>
                    <Tooltip
                        placement="left"
                        title={appContext.state.theme.palette.mode === "light"
                            ? languages[appContext.state.lang].OCEAN__MAIN_GLOBAL_THEMA_TEXT
                            : languages[appContext.state.lang].OCEAN__GLOBAL_THEMA_TEXT_DARK}
                    >
                        <IconButton className={classes.utilityButton} onClick={toggleTheme} size="large">
                            {appContext.state.theme.palette.mode === "light" ? <DarkModeTwoToneIcon/> : <LightModeTwoToneIcon/>}
                        </IconButton>
                    </Tooltip>
                </div>

                <Container maxWidth={false} className={`${classes.landingShell} ${classes.landingHeroShell}`}>
                    {isMobile ? (
                        <section className={classes.mobileHeroSection}>
                            <div className={classes.mobileHeroText}>
                                <Typography className={classes.mobileHeroTitle} component="h1" aria-label="Atlantis">
                                    <span className={classes.heroBrandCard}>
                                        <span className={classes.heroBrandName}>ATLANTIS</span>
                                        <span className={classes.heroBrandSubtitle}>
                                            Informazioni, richieste e prenotazioni in pochi passaggi, anche senza accesso.
                                        </span>
                                    </span>
                                </Typography>
                            </div>
                            <LandingVisuals classes={classes} compact/>
                        </section>
                    ) : (
                        <section className={classes.heroSection}>
                            <LandingVisuals classes={classes}/>
                            <div className={classes.heroContent}>
                                <Typography className={classes.heroTitle} component="h1" aria-label="Atlantis">
                                    <span className={classes.heroBrandCard}>
                                        <span className={classes.heroBrandName}>ATLANTIS</span>
                                        <span className={classes.heroBrandSubtitle}>
                                            Richieste, informazioni e prenotazioni in un unico punto di accesso pubblico, con percorsi chiari e immediati.
                                        </span>
                                    </span>
                                </Typography>
                            </div>
                        </section>
                    )}
                </Container>

                <Container maxWidth={false} className={`${classes.landingShell} ${classes.landingContentShell}`}>
                    {value && !selectedCategory ? faqSection : (
                        <section className={`${classes.contentSection} ${classes.landingPathsSection} ${isMobile ? classes.mobileContentSection : ""}`} aria-label="Categorie assistenza">
                            {selectedCategory ? (
                                <div className={`${classes.sectionHeading} ${classes.categoryDrilldownHeading} ${isMobile ? classes.mobileSectionHeading : ""}`}>
                                    <IconButton
                                        aria-label="Torna alle categorie"
                                        className={classes.categoryBackButton}
                                        onClick={() => setSelectedCategory(null)}
                                        size="large"
                                    >
                                        <ArrowBackTwoToneIcon/>
                                    </IconButton>
                                    <Typography component="h2" className={classes.sectionTitle}>
                                        {selectedCategory.label}
                                    </Typography>
                                </div>
                            ) : (
                                <div className={`${classes.sectionHeading} ${classes.categoryMainHeading} ${isMobile ? classes.mobileSectionHeading : ""}`}>
                                    <Typography component="h2" className={classes.sectionTitle}>
                                        Scegli il percorso
                                    </Typography>
                                    {searchBox}
                                </div>
                            )}
                            <div className={isMobile ? classes.mobileCategoryList : classes.categoryGrid}>
                                {(selectedCategory ? subCategoryItems : categoryItems).map((category) => (
                                    <CategoryButton
                                        category={category}
                                        classes={classes}
                                        compact={isMobile}
                                        key={category.key}
                                        onClick={category.onClick}
                                    />
                                ))}
                            </div>
                        </section>
                    )}
                </Container>
            </main>

            <Menu
                anchorEl={anchorEl}
                keepMounted
                onClose={() => handleLanguageClose()}
                open={Boolean(anchorEl)}
                slotProps={{
                    paper: {
                        className: classes.languageMenuPaper,
                    },
                }}
            >
                {Object.entries(langDict).map(([key, val]) => (
                    <MenuItem className={classes.languageItem} key={key} onClick={() => handleLanguageClose(key)}>
                        {val.flag}
                        <span>{val.label}</span>
                    </MenuItem>
                ))}
            </Menu>

            <Snackbar open={appContext.state.open} autoHideDuration={3000} onClose={handleCloseSnack}>
                <Alert onClose={handleCloseSnack} severity={appContext.state.severity}>
                    {appContext.state.message}
                </Alert>
            </Snackbar>
        </React.Fragment>
    );
};

function Alert(props) {
    return <MuiAlert elevation={16} variant="filled" {...props} />;
}

export default React.memo(LandingPage);
